import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Replay from '@mui/icons-material/Replay';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { useDashboardTourContext } from '../../pages/dashboardTour/DashboardTourContext';
import {
  setHasClosedCompleteOnboardingModal,
  setTouring,
} from '../../store/onboardingSlice';
import { IOnboarding } from '../OnboardingWalkthroughTabs';

const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    overflowY: 'visible',
    borderRadius: 10,
  },
  titleContainer: {
    paddingBottom: 0,
    paddingTop: theme.spacing(2),
  },
  titleContainerWithImage: {
    paddingTop: 100,
  },
  title: {
    fontSize: 36,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  titleImage: {
    position: 'absolute',
    top: -120,
    left: 'calc(50% - 132px)',
  },
  contentContainer: {
    fontSize: 16,
    lineHeight: 'normal',
  },
  errorsContainer: {
    position: 'relative',
  },
  errorsList: {
    backgroundColor: '#E5E8EC',
    color: '#555',
    paddingTop: theme.spacing(1),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    borderRadius: 3,
    overflow: 'hidden',
  },
  errorsExpanded: {
    paddingBottom: theme.spacing(6),
  },
  errorsCollapsed: {
    maxHeight: 142,
    paddingBottom: theme.spacing(2),
  },
  errorsOverlay: {
    position: 'absolute',
    zIndex: 10,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: 3,
    backgroundColor: 'transparent',
    backgroundImage:
      'linear-gradient(to bottom, rgba(255,0,0,0), rgba(229,232,236,1))',
  },
  errorsToggledExpandedBtn: {
    position: 'absolute',
    zIndex: 100,
    bottom: theme.spacing(1),
    left: 'calc(50% - 50px)',
    borderRadius: 100,
    backgroundColor: theme.palette.grey[700],
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.grey[600],
    },
  },
  entitiesCreatedList: {
    margin: 0,
    fontWeight: 'bold',
    listStyle: 'none',
  },
  dashboardLink: {
    textDecoration: 'underline',
    textTransform: 'uppercase',
    marginRight: theme.spacing(1),
    fontSize: '0.875rem',
  },
  dialogActions: {
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  nextStepsInfo: {
    background: '#edfaff',
    padding: '10px 15px',
    borderLeft: '3px solid #46B6D8',
    margin: '30px auto 0',
    maxWidth: '95%',
  },
  nextStepsInfoCopy: {
    fontSize: 13,
    marginBottom: 0,
  },
  loadingIcon: {
    background: '#fff',
    borderRadius: '50%',
    margin: '-80px auto 0',
    width: 150,
    height: 150,
    padding: 25,
    textAlign: 'center',
  },
}));

export interface ICompleteOnboardingModalProps {
  open: boolean;
  onClose: () => void;
  isOnboardingLoading: undefined | boolean;
  onboarding?: IOnboarding;
  isProcessingCompleteOnboarding: boolean;
  completeOnboardingRejectedReason?: Error;
  owWizardDataParsed: any;
  submitForm: () => void;
  cachedOnboarding?: IOnboarding;
}

export function CompleteOnboardingModal({
  open,
  onClose,
  isOnboardingLoading,
  onboarding,
  isProcessingCompleteOnboarding,
  completeOnboardingRejectedReason,
  owWizardDataParsed,
  submitForm,
  cachedOnboarding,
}: ICompleteOnboardingModalProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const onboardingSelector = useSelector((state) => state.onboarding);
  const { touring, status } = onboardingSelector;

  useEffect(() => {
    touring && onClose();
  }, [touring, onClose]);

  const getValue = (key: string) => {
    const value = onboarding?.[key] ?? cachedOnboarding?.[key];
    if (value) return value;

    switch (key) {
      case 'totalTeams':
        return owWizardDataParsed?.teams?.length ?? 0;
      case 'totalAgents':
        return owWizardDataParsed?.agents?.length ?? 0;
      case 'totalQueues':
        return owWizardDataParsed?.teams?.length ?? 0;
      case 'totalPowerlists':
        return (
          owWizardDataParsed?.teams?.filter(
            (team) => team.agentsCallListOfCustomers
          )?.length ?? 0
        );
      case 'totalCadences':
        return owWizardDataParsed?.teams?.length ?? 0;
      case 'totalIVRs':
        return 1;
      default:
        return 0;
    }
  };

  const errors = Array.from(
    new Set(
      onboarding?.errors ??
        (isOnboardingLoading ? cachedOnboarding?.errors : []) ??
        []
    )
  );

  const history = useHistory();

  const errorCount = errors.length + (completeOnboardingRejectedReason ? 1 : 0);

  const hasErrors = errorCount > 0;

  const isProcessing = React.useMemo(() => {
    return (
      (isOnboardingLoading
        ? cachedOnboarding?.status === 'processing'
        : onboarding?.status === 'processing') || isProcessingCompleteOnboarding
    );
  }, [
    isOnboardingLoading,
    cachedOnboarding,
    onboarding,
    isProcessingCompleteOnboarding,
  ]);

  // useEffect(() => {
  //   !isProcessing && hasClosedCompleteOnboardingModal && dispatch(setHasClosedCompleteOnboardingModal(false));
  // }, [isProcessing]);

  const { setState } = useDashboardTourContext();
  const handleClickStart = () => {
    !isProcessing &&
      status === 'complete' &&
      dispatch(setHasClosedCompleteOnboardingModal(true));
    history.push('/call-reporting/business-snapshot/');
    dispatch(setTouring(true));
    setTimeout(() => {
      setState({ run: true, tourActive: true, stepIndex: 0 });
    });
    onClose();
  };

  const [isErrorsExpanded, setIsErrorsExpanded] = useState(false);

  const toggleExpanded = () => setIsErrorsExpanded(!isErrorsExpanded);

  return (
    <Dialog
      open={open}
      aria-labelledby="form-dialog-title"
      maxWidth="sm"
      fullWidth={true}
      PaperProps={{ className: classes.dialogPaper }}
      onClose={onClose}
    >
      <DialogTitle
        className={`${classes.titleContainer} ${
          !isProcessing ? classes.titleContainerWithImage : ''
        }`}
      >
        {isProcessing ? (
          <div className={classes.loadingIcon}>
            <CircularProgress size={100} style={{ verticalAlign: 'center' }} />
          </div>
        ) : (
          <img
            src="/media/onboarding/success-2.svg"
            alt="Success"
            height="200"
            className={classes.titleImage}
          />
        )}
        <Typography className={classes.title}>
          {isProcessing ? 'Building your account' : 'Success!'}
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.contentContainer}>
        {hasErrors ? (
          <>
            <p>
              Your account is now set up, but we ran into{' '}
              {errorCount > 1 ? 'a few errors' : 'an error'} along the way.
              Click expand to see{' '}
              {errorCount > 1 ? 'the full list' : 'the error'} and please
              contact <a href="mailto:support@kixie.com">support@kixie.com</a>{' '}
              with any questions.
            </p>
            <div className={classes.errorsContainer}>
              <ul
                className={`${classes.errorsList} ${
                  isErrorsExpanded
                    ? classes.errorsExpanded
                    : classes.errorsCollapsed
                }`}
              >
                {completeOnboardingRejectedReason ? (
                  <li>{completeOnboardingRejectedReason}</li>
                ) : null}
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
              {isErrorsExpanded ? null : (
                <div className={classes.errorsOverlay} />
              )}
              <Button
                variant="contained"
                startIcon={
                  isErrorsExpanded ? <ArrowUpward /> : <ArrowDownward />
                }
                className={classes.errorsToggledExpandedBtn}
                color="inherit"
                onClick={toggleExpanded}
              >
                {isErrorsExpanded ? 'Collapse' : 'Expand'}
              </Button>
            </div>
            {!completeOnboardingRejectedReason ? (
              <p>
                With the information provided, we attempted to create the
                following:
              </p>
            ) : null}
          </>
        ) : isProcessing ? (
          <>
            <p>
              You have completed your onboarding walkthrough! We'll notify you
              once your account is setup.
            </p>
            <p>
              With the information you provided, we're building the following:
            </p>
          </>
        ) : (
          <>
            <p>
              Your account is now set up and you're ready to start using Kixie
              to help you make more calls and scale your business.
            </p>
            <p>With the information provided, we've created the following:</p>
          </>
        )}
        {isProcessing || !completeOnboardingRejectedReason ? (
          <ul className={classes.entitiesCreatedList}>
            <li>
              {getValue('queues')} / {getValue('totalQueues')}{' '}
              {getValue('totalQueues') > 1 ? 'Queues' : 'Queue'}
            </li>
            <li>
              {getValue('agents')} / {getValue('totalAgents')}{' '}
              {getValue('totalAgents') > 1 ? 'Agents' : 'Agent'}
            </li>
            <li>
              {getValue('teams')} / {getValue('totalTeams')}{' '}
              {getValue('totalTeams') > 1 ? 'Teams' : 'Team'}
            </li>
            <li>
              {getValue('powerlists')} / {getValue('totalPowerlists')}{' '}
              {getValue('totalPowerlists') > 1 ? 'PowerLists' : 'PowerList'}
            </li>
            <li>
              {getValue('cadences')} / {getValue('totalCadences')}{' '}
              {getValue('totalCadences') > 1 ? 'Cadences' : 'Cadence'}
            </li>
            <li>
              {getValue('ivrs')} / {getValue('totalIVRs')}{' '}
              {getValue('totalIVRs') > 1 ? 'IVRs' : 'IVR'}
            </li>
          </ul>
        ) : null}
        {!isProcessing ? (
          <div className={classes.nextStepsInfo}>
            <p className={classes.nextStepsInfoCopy}>
              Click <strong>BEGIN TOUR</strong> to run through important pages
              and features.
              <br />
              To skip the tour for now, click{' '}
              <strong>RETURN TO DASHBOARD</strong>.
            </p>
          </div>
        ) : null}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          className={classes.dashboardLink}
          color="primary"
          onClick={() => {
            onboarding?.status === 'complete' &&
              dispatch(setHasClosedCompleteOnboardingModal(true));
            history.push('/call-reporting/business-snapshot/');
          }}
        >
          Return To Dashboard
        </Button>

        {completeOnboardingRejectedReason?.message === 'Network Error' ? (
          <Button
            endIcon={<Replay />}
            variant="contained"
            color="primary"
            onClick={() => {
              submitForm();
            }}
          >
            Retry
          </Button>
        ) : (
          <Button
            component={RouterLink}
            to="/"
            onClick={handleClickStart}
            endIcon={<ArrowForward />}
            variant="contained"
            color="primary"
          >
            Begin Tour
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
